// Inline Verify lifecycle. Submission reads the live token through the shared adapter.
import { useCallback, useEffect, useRef, useState } from 'react';

import { waitForVerification } from '@sstk/verify-widget/form';
import { createVerifyElement, loadVerifyWidget } from '../services/captcha';

export interface VerifyGate {
  /** One-use token, '' until solved and again once spent, expired or renewed. */
  token: string;
  /** The widget script could not be loaded at all (offline, blocked). */
  unavailable: boolean;
  /** Legacy compatibility flag. Human challenge completion has no deadline. */
  stalled: boolean;
  /** Discard a spent token. The next interaction starts a fresh check. */
  renew: () => void;
  verify: () => Promise<string>;
  /** Ref callback for the element the widget mounts into. The widget exists
   * only while that element does, so rendering it conditionally works. */
  hostRef: (node: HTMLDivElement | null) => void;
}

export function useVerifyGate(siteKey: string, startOnMount = false): VerifyGate {
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [token, setToken] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const controller = useRef<AbortController | null>(null);
  const widgetRef = useRef<ReturnType<typeof createVerifyElement> | null>(null);

  useEffect(() => {
    if (!host) return;
    let cancelled = false;
    const abort = new AbortController();
    controller.current = abort;
    // The custom element extends HTMLElement, so it is only ever loaded here,
    // client side, never at module scope.
    loadVerifyWidget()
      .then(() => {
        if (cancelled) return;
        const widget = createVerifyElement(siteKey);
        widget.addEventListener('sstk-verified', (event) => {
          const detail = (event as CustomEvent<{ token: string; expiresAt: number }>).detail;
          if (detail.token && detail.expiresAt > Date.now()) setToken(detail.token);
        });
        // The widget resets itself to idle when its token expires; the form
        // must stop offering the dead token.
        widget.addEventListener('sstk-expired', () => setToken(''));
        host.append(widget);
        widgetRef.current = widget;
        if (startOnMount) widget.start();
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
      abort.abort();
      widgetRef.current?.remove();
      widgetRef.current = null;
      setToken('');
      setUnavailable(false);
    };
  }, [host, siteKey, startOnMount]);

  const renew = useCallback(() => {
    setToken('');
    if (widgetRef.current?.token) widgetRef.current.reset();
  }, []);
  const verify = useCallback(async () => {
    const widget = widgetRef.current;
    if (!widget) throw new Error('The security check is still loading. Please retry.');
    return waitForVerification(widget, { signal: controller.current?.signal });
  }, []);
  return { token, unavailable, stalled: false, renew, verify, hostRef: setHost };
}

export function VerifyWidget({ gate }: { gate: VerifyGate }) {
  return (
    <div>
      <div ref={gate.hostRef} />
      {(gate.unavailable || gate.stalled) && (
        <p role="alert" style={{ color: 'var(--ss-danger)', fontSize: 13, lineHeight: 1.5, marginTop: 8 }}>
          {gate.unavailable
            ? 'The verification check could not load. Check your connection and reload the page.'
            : 'Verification is taking longer than expected. If nothing appears above, check your connection or reload the page.'}
        </p>
      )}
    </div>
  );
}
